import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { CheckCircle, Error, RadioButtonUnchecked } from '@mui/icons-material';
import type { AgentStep } from '../../store/slices/agentProgressSlice';

const STEP_LABELS: Record<string, string> = {
  // Plan phase
  fetch_file:        'Fetch file from GitHub',
  generate_plan:     'We are generating a refactor plan',
  // PR phase
  load_context:      'Load plan context',
  write_modules:     'Write modules',
  completeness_check:'Completeness check',
  validate_imports:  'Validate imports',
  fix_imports:       'Fix broken imports',
  write_facade:      'Write delegation facade',
  update_callers:    'Update caller imports',
  create_pr:         'Open pull request',
  // Fix phase
  prepare:           'Preparing issues',
  generate_fixes:    'We are generating fixes',
};

interface Props {
  steps: AgentStep[];
}

const StepIcon: React.FC<{ status: AgentStep['status'] }> = ({ status }) => {
  if (status === 'done')    return <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />;
  if (status === 'failed')  return <Error sx={{ fontSize: 18, color: 'error.main' }} />;
  if (status === 'running') return <CircularProgress size={16} thickness={5} sx={{ color: 'primary.main' }} />;
  return <RadioButtonUnchecked sx={{ fontSize: 18, color: 'text.disabled' }} />;
};

const AgentStepTimeline: React.FC<Props> = ({ steps }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
    {steps.map((s) => (
      <Box key={s.step} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <StepIcon status={s.status} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={s.status === 'running' ? 600 : 400}
            color={s.status === 'failed' ? 'error.main' : s.status === 'done' ? 'text.secondary' : 'text.primary'}
            noWrap
          >
            {STEP_LABELS[s.step] ?? s.step}
          </Typography>
          {s.status === 'running' && s.stepTotal && (
            <Typography variant="caption" color="text.disabled">
              {s.stepIndex} of {s.stepTotal}
            </Typography>
          )}
          {s.status === 'failed' && (
            <Typography variant="caption" color="error.main" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
              {s.message}
            </Typography>
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

export default AgentStepTimeline;
